import { compose } from 'lodash/fp';
import { connect } from 'react-redux';
import { Component } from 'react';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { TextKey, RouteName, ActionType, FeatureFlag } from '@mews-commander/enums';
import { FeatureFlagService, LocalizedText, lt, withFeatureFlagService } from '@mews/framework';
import { IconName, InputValue, withBreakpointRange } from '@optimus-web/core';
import { isDesktopOrLargeDesktop, isMobileOrSmallPhone, WithBreakpointRange } from '@mews-commander/ui';
import { ApplicationState, Interval } from '@mews-commander/types';
import { Resource } from '@mews-commander/resources';
import { ResourceBlockType } from '@mews-commander/data-access';
import { getResolveRoute } from '@mews-commander/legacy-app-instance';
import { compactArray } from '@mews/fp';
import { hasMultipleBookableServices } from '@mews-commander/legacy-entities/src/ducks/services';
import {
    constructResourcesTree,
    ResourceBlockResourceUse,
} from '@mews-commander/legacy-entities/src/ducks/resourcesTree';
import { getGroupOrAccountBillingUrl, openGroupOrAccountBillingModal } from '../../navigation/billing';
import { ReservationSimpleDetail, ResourceBlockSimpleDetail } from '../../simpleDetail/containers';
import { showReservation, showGroup, getSimpleDetailMode } from '../../simpleDetail/module';
import { SIMPLE_DETAIL_SCOPE } from '../constants';
import { NavigationDirection, TimelineCanvas } from '../types';
import { sortResourcesByPosition } from '../utils/resourceUse';
import { DialogBackendPage } from '../../app/components/DialogBackendPage';
import { getLoaderVisible, pageLoaderStore } from '../../../core/pageLoader';
import { FilterSidebarLayout } from '../../../components/layout';
import {
    deselectResourceUse,
    optimizeAssignment,
    previewAssignment,
    cancelAssignment,
    updateAfterHouseUseChange,
} from '../actions';
import {
    timelineFilterSchema,
    TimelineFilterSchemaKeys,
    TimelineFilterValues,
    TimelineFilterValuesExtended,
    timelineUrlFilterSchema,
} from '../components/TimelineFilters/timelineFilterSchema';
import { AvailabilityWarningDialog } from '../components/AvailibilityWarningDialog';
import TimelineControlBar from '../components/ControlBar/TimelineControlBar';
import PreviewDialog from '../components/PreviewDialog';
import { TimelineFilterFields } from '../components/TimelineFilters/TimelineFilterFields';
import {
    isLoading,
    isAssigningResource,
    getSelectedResourceUse,
    isSidebarOpened,
    hasEditButton,
    isOptimisationInProgress,
    getResourceMovements,
    isPreviewDialogOpened,
    isFetchingTimeline,
} from '../reducers';
import { openCustomerDetail } from '../../app/actions/customer';
import { NewReservationInfo } from '../../newReservation/components/NewReservationPanel/types';
import { NewReservationPanelWrapper } from '../../newReservation/components/NewReservationPanel';
import { CanvasWrapper } from '../containers/CanvasWrapper';

interface Props extends WithBreakpointRange {
    resources: ReturnType<typeof constructResourcesTree>;
    handleNewHouseUse: $TSFixMeFunction;
    handleAssignResourceLocked: $TSFixMeFunction;
    isLoading: boolean;
    isFetching: boolean;
    isAssigningResource: boolean;
    isOptimisationInProgress: boolean;
    selectedResourceUse: ResourceBlockResourceUse | undefined;
    serviceId: string;
    deselectResourceUse: $TSFixMeFunction;
    getUtcDateString: $TSFixMeFunction;
    sidebarOpened?: boolean;
    handleCustomerDetail: $TSFixMeFunction;
    hasEditButton?: boolean;
    handleEditResourceBlock?: $TSFixMeFunction;
    guideLink: string;
    onOptimizeAssignment: $TSFixMeFunction;
    onPreviewAssignment: $TSFixMeFunction;
    onCancelAssignment: $TSFixMeFunction;
    simpleDetailMode: string;
    resourceMovementsCount?: number;
    isPreviewDialogOpened: boolean;
    selectedDensity: number | string;
    selectedDateTime?: string;
    featureFlagService: FeatureFlagService;
    updateAfterNewHouseUse: (data: { id: string }) => void;
    selectedReservationId?: string;
    selectedBlockId?: string;
    defaultNewReservationInfo?: NewReservationInfo;
    newReservationPlaceholderLabel: string;

    // filter stuff here only bcs of pass it to connect above
    filterValues: TimelineFilterValues;
    defaultFilterValues: TimelineFilterValues;
    onFilterChanged: (filterValues: TimelineFilterValuesExtended) => void;
}

interface State {
    resourcesToHighlight: Resource[];
    searchedSpace: Resource | null;
    houseUseDialogOpened: boolean;
    isLoaderVisible: boolean;
    newReservationInfo: null | NewReservationInfo;
}

class TimelinePage extends Component<Props, State> {
    canvas: TimelineCanvas | undefined;

    unsubscribeFromPageLoader?: () => void;

    constructor(props: Props) {
        super(props);
        this.state = {
            resourcesToHighlight: [],
            searchedSpace: null,
            houseUseDialogOpened: false,
            isLoaderVisible: getLoaderVisible(),
            newReservationInfo: props.defaultNewReservationInfo ?? null,
        };
    }

    handleDateChanged = (date: $TSFixMe) => {
        const utcDateString = this.props.getUtcDateString(date);
        if (utcDateString) {
            this.canvas?.handleGoToDate(utcDateString);
        }
    };

    handleHouseUseDialogToggle = () => {
        this.setState((prevState) => ({ houseUseDialogOpened: !prevState.houseUseDialogOpened }));
    };

    handleHouseUseDialogSubmit = (data?: { id: string }) => {
        this.handleHouseUseDialogToggle();
        if (data) {
            this.props.updateAfterNewHouseUse(data);
        }
    };

    componentDidMount() {
        this.unsubscribeFromPageLoader = pageLoaderStore.subscribe((state) => {
            this.setState({ isLoaderVisible: state.loaderVisible });
        });

        if (this.props.selectedReservationId) {
            this.canvas?.handleGoToResourceUse(this.props.selectedReservationId, true);
        }

        if (this.props.selectedBlockId) {
            this.canvas?.handleGoToResourceUse(this.props.selectedBlockId, false);
        }
    }

    componentWillUnmount() {
        if (this.unsubscribeFromPageLoader) {
            this.unsubscribeFromPageLoader();
        }
    }

    handleCloseNewReservationPanel = () => {
        this.setState((s) => ({ ...s, newReservationInfo: null }));
        this.canvas?.clearConfirmedResourceUsePlaceholder();
    };

    handleNewReservationDatesChange = (interval: Interval) => {
        this.setState((s) => ({
            newReservationInfo: s.newReservationInfo && { ...s.newReservationInfo, ...interval },
        }));
    };

    render() {
        const {
            selectedResourceUse,
            resources,
            sidebarOpened,
            handleCustomerDetail,
            // @ts-expect-error $TSFixMeError: Property 'handleOpenBilling' does not exist on type 'Readonly<Props> & Readonly<{ children?: ReactNode; }>'.
            handleOpenBilling,
            breakpointRange,
            guideLink,
            // @ts-expect-error $TSFixMeError: Property 'selectReservation' does not exist on type 'Readonly<Props> & Readonly<{ children?: ReactNode; }>'.
            selectReservation,
            // @ts-expect-error $TSFixMeError: Property 'selectGroup' does not exist on type 'Readonly<Props> & Readonly<{ children?: ReactNode; }>'.
            selectGroup,
            simpleDetailMode,
            resourceMovementsCount,
            onCancelAssignment,
            onOptimizeAssignment,
            // @ts-expect-error $TSFixMeError: Property 'hasMultipleServices' does not exist on type 'Readonly<Props> & Readonly<{ children?: ReactNode; }>'.
            hasMultipleServices,
            // @ts-expect-error $TSFixMeError: Property 'groupOrAccountBillingUrl' does not exist on type 'Readonly<Props> & Readonly<{ children?: ReactNode; }>'.
            groupOrAccountBillingUrl,
            // @ts-expect-error $TSFixMeError: Property 'enterpriseId' does not exist on type 'Readonly<Props> & Readonly<{ children?: ReactNode; }>'.
            enterpriseId,
            selectedDensity,
            selectedDateTime,
            featureFlagService,
        } = this.props;

        const isTimelineSpaceSearchNavigationEnabled = featureFlagService.hasFlag(
            FeatureFlag.timelineSpaceSearchNavigationEnabled
        );

        const isMobile = isMobileOrSmallPhone(breakpointRange);
        const isDesktopDevice = isDesktopOrLargeDesktop(breakpointRange);

        const resolveRoute = getResolveRoute();

        const reservationActions =
            selectedResourceUse && selectedResourceUse.isReservation
                ? [
                      {
                          type: ActionType.Link,
                          actionId: 'profile',
                          text: <LocalizedText textKey={TextKey.Profile} />,
                          icon: IconName.Profile,
                          url: resolveRoute(RouteName.CustomerDetail, { id: selectedResourceUse.customerId }),
                          onClick() {
                              handleCustomerDetail(selectedResourceUse.customerId);
                          },
                      },
                      {
                          type: ActionType.Link,
                          actionId: 'billing',
                          text: <LocalizedText textKey={TextKey.Billing} />,
                          icon: IconName.PaymentReport,
                          url: groupOrAccountBillingUrl,
                          onClick() {
                              handleOpenBilling({
                                  accountId: selectedResourceUse.customerId,
                                  // @ts-expect-error $TSFixMeError: Property 'groupId' does not exist on type '{}'.
                                  groupId: selectedResourceUse.groupId,
                              });
                          },
                      },
                  ]
                : [];

        const simpleDetailProps = {
            opened: sidebarOpened,
            resourceUse: selectedResourceUse,
            onClose: this.props.deselectResourceUse,
            closeOnBackdropClick: false,
            modal: false,
            disableClickAway: true,
        };

        const SimpleDetail =
            selectedResourceUse &&
            (selectedResourceUse.isReservation ? (
                <ReservationSimpleDetail
                    {...simpleDetailProps}
                    // @ts-expect-error $TSFixMeError: Type '{ actions: ({ type: ActionType; actionId: string; text: Element; value: any; onBackgroundIcon: IconName; offBackgr...
                    actions={reservationActions}
                    showReservation={selectReservation}
                    showGroup={selectGroup}
                    scope={SIMPLE_DETAIL_SCOPE}
                    mode={simpleDetailMode}
                    onNavigateToSpaceUse={this.canvas?.handleGoToResourceUse}
                    withGroupDetail
                />
            ) : (
                <ResourceBlockSimpleDetail
                    {...simpleDetailProps}
                    // @ts-expect-error $TSFixMeError: Type 'boolean | undefined' is not assignable to type 'boolean'.
                    hasEditButton={this.props.hasEditButton}
                />
            ));

        const handleResourceSearch = (searchValue: InputValue) => {
            const filteredResources = pipe(
                resources.resources,
                array.filter(
                    (resource) =>
                        !!resource.name && resource.name.toLowerCase().includes(String(searchValue).toLowerCase())
                ),
                sortResourcesByPosition
            );

            const resourcesToHighlight = searchValue ? filteredResources : [];
            const searchedSpace =
                isTimelineSpaceSearchNavigationEnabled && resourcesToHighlight.length > 0
                    ? resourcesToHighlight[0]
                    : null;

            this.setState({
                resourcesToHighlight,
                searchedSpace,
            });

            if (array.isNonEmpty(resourcesToHighlight)) {
                this.canvas?.handleGoToResource(resourcesToHighlight[0].name);
            }
        };

        const navigateHighlightedResources = (direction: NavigationDirection) => {
            const { resourcesToHighlight, searchedSpace } = this.state;
            const currentHighlightedResourceIndex = resourcesToHighlight.findIndex(
                (resource) => resource.id === searchedSpace?.id
            );

            const step = direction === NavigationDirection.Next ? 1 : -1;
            const nextIndex = currentHighlightedResourceIndex + step;

            if (nextIndex < 0 || nextIndex === resourcesToHighlight.length) return;

            const nextResource = resourcesToHighlight[nextIndex];

            this.setState({ searchedSpace: nextResource });
            this.canvas?.handleGoToResource(nextResource.name);
        };

        const isOptimizeSpaceAllocationDisabled =
            this.props.isOptimisationInProgress || this.props.isPreviewDialogOpened;

        const loading =
            this.props.isAssigningResource ||
            this.props.isOptimisationInProgress ||
            this.state.isLoaderVisible ||
            this.props.isLoading ||
            this.props.isFetching;

        return (
            <FilterSidebarLayout
                withOverlayLoader
                toggleAfterSubmit
                withClearFilters
                padded={false}
                filterSidebarOpenedByDefault={false}
                guideLink={guideLink}
                loading={loading}
                primaryAction={
                    isMobile
                        ? {
                              'data-test-icon-button': IconName.Menu,
                              text: lt('ResourceCategoryPlural'),
                              type: ActionType.Link,
                              icon: IconName.Menu,
                              onClick: () => this.canvas?.handleToggleResourceLegend(),
                          }
                        : null
                }
                actions={compactArray([
                    !isDesktopDevice
                        ? {
                              type: ActionType.Custom,
                              actionId: 'optimizeSpaceAllocation',
                              text: lt('OptimizeSpaceAllocation'),
                              icon: IconName.CalendarStars,
                              disabled: isOptimizeSpaceAllocationDisabled,
                              onClick: this.props.onPreviewAssignment,
                          }
                        : null,
                    {
                        type: ActionType.Custom,
                        actionId: 'houseUse',
                        text: lt('HouseUse'),
                        icon: IconName.HouseUse,
                        onClick: this.handleHouseUseDialogToggle,
                    },
                ])}
                headerContent={
                    this.canvas && (
                        <TimelineControlBar
                            serviceId={this.props.filterValues[TimelineFilterSchemaKeys.ServiceId]}
                            resourcesToHighlight={this.state.resourcesToHighlight}
                            searchedSpace={this.state.searchedSpace}
                            isOptimizeSpaceAllocationDisabled={isOptimizeSpaceAllocationDisabled}
                            onOptimizeSpaceAllocationClick={this.props.onPreviewAssignment}
                            onPrevFast={this.canvas.handlePrevFast}
                            onPrev={this.canvas.handlePrev}
                            onToday={this.canvas.handleToday}
                            onNext={this.canvas.handleNext}
                            onNextFast={this.canvas.handleNextFast}
                            onResourceSearch={handleResourceSearch}
                            navigateHighlightedResources={navigateHighlightedResources}
                            onDateChange={this.handleDateChanged}
                            loading={loading}
                        />
                    )
                }
                // @ts-expect-error zod infer wrong type when transform func is present in schema
                filterSchema={timelineFilterSchema}
                queryParamsSchema={timelineUrlFilterSchema}
                defaultFilterValues={this.props.defaultFilterValues}
                onFilterSubmit={this.props.onFilterChanged}
                submitButtonLabel={lt('ViewTimeline')}
                filterSidebar={{
                    title: lt('Filter'),
                    fields: (
                        <TimelineFilterFields
                            currentServiceId={this.props.filterValues[TimelineFilterSchemaKeys.ServiceId]}
                        />
                    ),
                }}
            >
                {/* @ts-expect-error */}
                <CanvasWrapper
                    ref={(c: TimelineCanvas) => (this.canvas = c)}
                    resources={resources}
                    searchedSpace={this.state.searchedSpace}
                    resourcesToHighlight={this.state.resourcesToHighlight}
                    isMobile={isMobile}
                    timelineTouchScreenTest={featureFlagService.hasFlag(FeatureFlag.timelineTouchScreenTest)}
                    selectedDensity={selectedDensity}
                    selectedDateTime={selectedDateTime}
                    // serviceId has to be passed like this due to getTimelineResourceUsesAndClustersOptimized in connect
                    serviceId={this.props.filterValues[TimelineFilterSchemaKeys.ServiceId]}
                    filterValues={this.props.filterValues}
                    // hasMultipleServices has to be passed like this due to getTimelineResourceUsesAndClustersOptimized in connect
                    hasMultipleServices={hasMultipleServices}
                    handleConfirmNewReservation={(value) =>
                        this.setState((previousState) => ({ ...previousState, newReservationInfo: value }))
                    }
                    confirmedResourceUsePlaceholder={this.state.newReservationInfo}
                    newReservationPlaceholderLabel={this.props.newReservationPlaceholderLabel}
                    // eslint-disable-next-line react/jsx-handler-names
                    handleCloseNewReservationPanel={this.handleCloseNewReservationPanel}
                />
                {SimpleDetail}
                <PreviewDialog
                    active={this.props.isPreviewDialogOpened && !this.props.isOptimisationInProgress}
                    onCloseDialog={onCancelAssignment}
                    onApplyOptimize={onOptimizeAssignment}
                    // @ts-expect-error $TSFixMeError: Type 'number | undefined' is not assignable to type 'number'.
                    resourceMovementsCount={resourceMovementsCount}
                />
                <AvailabilityWarningDialog />
                <DialogBackendPage<{ id: string }>
                    routeName={RouteName.ResourceBlockCreate}
                    params={{
                        query: {
                            ownerId: enterpriseId,
                            type: Object.keys(ResourceBlockType).indexOf(ResourceBlockType.InternalUse),
                        },
                    }}
                    title={lt('HouseUse')}
                    opened={this.state.houseUseDialogOpened}
                    onClose={this.handleHouseUseDialogToggle}
                    onSubmit={this.handleHouseUseDialogSubmit}
                />
                {this.state.newReservationInfo && (
                    <NewReservationPanelWrapper
                        serviceId={this.props.filterValues[TimelineFilterSchemaKeys.ServiceId]}
                        newReservationInfo={this.state.newReservationInfo}
                        onClose={this.handleCloseNewReservationPanel}
                        onDatesChange={this.handleNewReservationDatesChange}
                    />
                )}
            </FilterSidebarLayout>
        );
    }
}

export default compose(
    connect(
        // @ts-expect-error $TSFixMeError: Property 'enterpriseId' does not exist on type '{}'.
        (state: ApplicationState, { enterpriseId, selectedResourceUse }) => ({
            isLoading: isLoading(state),
            isFetching: isFetchingTimeline(state),
            isAssigningResource: isAssigningResource(state),
            hasEditButton: hasEditButton(state),
            resourceMovementsCount: getResourceMovements(state),
            selectedResourceUse: getSelectedResourceUse(state),
            sidebarOpened: isSidebarOpened(state),
            isOptimisationInProgress: isOptimisationInProgress(state),
            isPreviewDialogOpened: isPreviewDialogOpened(state),
            simpleDetailMode: getSimpleDetailMode(state, { scope: SIMPLE_DETAIL_SCOPE }),
            hasMultipleServices: hasMultipleBookableServices(state, { enterpriseId }),
            groupOrAccountBillingUrl:
                selectedResourceUse && selectedResourceUse.isReservation
                    ? getGroupOrAccountBillingUrl(state, {
                          groupId: selectedResourceUse.groupId,
                          accountId: selectedResourceUse.accountId,
                      })
                    : null,
        }),
        {
            selectReservation: showReservation,
            selectGroup: showGroup,
            handleOpenBilling: openGroupOrAccountBillingModal,
            deselectResourceUse,
            updateAfterNewHouseUse: updateAfterHouseUseChange,
            handleCustomerDetail: openCustomerDetail,
            onCancelAssignment: cancelAssignment,
            onOptimizeAssignment: optimizeAssignment,
            onPreviewAssignment: previewAssignment,
        }
    ),
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    withBreakpointRange,
    withFeatureFlagService,
    withBreakpointRange
    // @ts-expect-error $TSFixMeError: Expected 0 arguments, but got 1.
)(TimelinePage);
